/* eslint-disable @typescript-eslint/no-unsafe-call */
import type { ZTableColumnProps } from "@/shared/components/table";

import { ZButton } from "@/shared/components/button";
import { ZTableDeprecrated } from "@/shared/components/table-deprecrated/TableDeprecrated";
import capitalizeFirstLetter from "@/shared/utils/capitalize-first-letter";
import { cn } from "@/shared/utils/classname-merger";
import cloneDeep from "lodash/cloneDeep";
import type { ChangeEvent } from "react";
import { useCallback, useMemo, useState } from "react";
import { RenderCellTypeEnum, tableConfigs } from "../../../constants/helpers";
import type {
  AgentSpecificTableRowData,
  ConfidenceTypes
} from "../../../types/index.ts";
import { MultiVendorAssessmentTypes } from "../../../types/index.ts";
import type { AGENT_TYPES } from "../../../types/index.ts";
import { renderTableComponents } from "../../table-components";
import { useVendorRiskAssessment } from "../useVendorRiskAssessment/useVendorRiskAssessment";
import { useVendorRiskAssessmentStore } from "../useVendorRiskAssessment/useVendorRiskAssessmentStore";
import AddPotentialIssuePopup from "./AddPotentialIssuePopup";
import { PotentialIssueKebabMenu } from "./PotentialIssueKebabMenu";
import DeleteConfirmationModal from "./DeleteConfirmationModal";

import { useAgentSelector, useAgentStepData } from "@/modules/agent/states";
import type { VendorRiskResponseItemForPotentialIssues, VendorPotentialIssueItem } from "@/modules/agent/types/vendorPotentialIssues.ts";
import { AgentSessionStatus, AgentSessionStepType } from "@/modules/sessions/types";
import { addNotification } from "@/shared/states/notification";
import { useParams } from "react-router-dom";
import type { IdParams } from "../Overview/Overview";
import { Input } from "@nextui-org/react";
import { TablePagination } from "@/modules/sessions/components/TablePagination";
import type { VendorRiskSectionEnum } from "@/modules/agent/types/vendorAssessment.ts";

type AddPotentialIssuePopupMode =
  | { mode: "CLOSED" }
  | { mode: "ADD" }
  | {
    mode: "EDIT",
    issueType: VendorRiskSectionEnum.potential_issues;
    indexInIssuesArray: number,
  }

type DeleteConfirmationState = 
  | { open: false }
  | { 
    open: true, 
    item: ExtendedVendorRiskResponseItem, 
    indexInValueArray: number 
  };

// Type definition for the potential issue structure
interface PotentialIssueItem {
  gap?: {
    value: string;
    severity?: string;
    sources?: Array<unknown>;
  };
  recommendation?: {
    value: string;
    sources?: Array<unknown>;
  };
  severity?: string;
  confidence?: ConfidenceTypes;
  sources?: Array<unknown>;
  control_title?: string;
}

// Extend vendor risk response item to support the structure
interface ExtendedVendorRiskResponseItem extends VendorRiskResponseItemForPotentialIssues {
  value: {
    type: string;
    value: VendorPotentialIssueItem[];
    potential_issues: PotentialIssueItem[];
  };
}

const PotentialIssues = () => {
  const { id } = useParams<keyof IdParams>() as IdParams;

  const [addIssuePopupMode, setAddIssuePopupMode] = useState<AddPotentialIssuePopupMode>({ mode: "CLOSED" });
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmationState>({ open: false });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { vendorPotentialIssuesData } = useVendorRiskAssessment();
  const { setVendorPotentialIssues } = useVendorRiskAssessmentStore();
  const stepData = useAgentStepData(id);
  const { setSelectedId } = useAgentSelector.useActions();

  const isReportReady = useMemo(() => {
    return stepData?.find((step) => step.type === AgentSessionStepType.PREPARE_REPORT)?.status === AgentSessionStatus.COMPLETE;
  }, [stepData]);

  const handleDelete = useCallback((item: ExtendedVendorRiskResponseItem, indexInValueArray: number) => {
    if (isReportReady) {
      return;
    }

    // Show delete confirmation modal instead of immediately deleting
    setDeleteConfirmation({ open: true, item, indexInValueArray });
  }, [isReportReady]);

  // Function to perform actual deletion after confirmation
  const confirmDelete = useCallback(() => {
    if (!deleteConfirmation.open) return;
    
    const { item, indexInValueArray } = deleteConfirmation;
    
    const temp = cloneDeep(vendorPotentialIssuesData) as ExtendedVendorRiskResponseItem[];
    const findkeyIndex = temp.findIndex((d) => d.key === item.key);
    if (findkeyIndex > -1) {
      temp[findkeyIndex].value.potential_issues.splice(indexInValueArray, 1);
      setSelectedId(id, temp[findkeyIndex].key);
    }
    setVendorPotentialIssues(temp);
    addNotification({
      type: "success",
      message: "Potential issue deleted successfully",
    });
    
    // Close the modal
    setDeleteConfirmation({ open: false });
  }, [deleteConfirmation, vendorPotentialIssuesData, setSelectedId, id, setVendorPotentialIssues]);

  const handleEdit = useCallback((item: ExtendedVendorRiskResponseItem, indexInValueArray: number) => {
    if (isReportReady) {
      return;
    }

    setAddIssuePopupMode({
      mode: "EDIT",
      issueType: item.key,
      indexInIssuesArray: indexInValueArray,
    });
  }, [isReportReady]);

  const rows = useMemo(() => {
    const newRowList: IPotentialIssue[] = [];
    
    if (!vendorPotentialIssuesData || !Array.isArray(vendorPotentialIssuesData)) {
      return newRowList;
    }
    
    // biome-ignore lint/complexity/noForEach: <explanation>
    (vendorPotentialIssuesData as unknown as ExtendedVendorRiskResponseItem[]).forEach((categoryWisePotentialIssuesList) => {
      if (categoryWisePotentialIssuesList.value?.potential_issues?.length) {
        // Create array of potential issues in this category
        const categoryRows: IPotentialIssue[] = [];
        
        categoryWisePotentialIssuesList.value.potential_issues.forEach((potentialIssueItem: PotentialIssueItem, idx: number) => {
          if (!potentialIssueItem.gap || !potentialIssueItem.recommendation) {
            return;
          }
          
          const rowItem = {
            id: `${categoryWisePotentialIssuesList.key};${idx}`,
            confidence: potentialIssueItem.confidence || '',
            recommendation: potentialIssueItem.recommendation?.value || '',
            gap: potentialIssueItem.gap?.value || '',
            control_title: potentialIssueItem.control_title || '',
            type: capitalizeFirstLetter(categoryWisePotentialIssuesList.key),
            severity: potentialIssueItem.severity || '',
            handleDelete: () => handleDelete(categoryWisePotentialIssuesList, idx),
            handleEdit: () => handleEdit(categoryWisePotentialIssuesList, idx),
            isReportReady,
          } as IPotentialIssue;

          categoryRows.push(rowItem);
        });
        
        // Add all rows from this category at the beginning of the main list
        // This ensures that for each category, items maintain their internal order (newest first)
        newRowList.unshift(...categoryRows);
      }
    });

    return newRowList;
  }, [vendorPotentialIssuesData, handleDelete, handleEdit, isReportReady]);

  // Combine search and filteredRows into one memoized value
  const filteredRows = useMemo(() => {
    // First get the base rows - either all rows or filtered by search
    const result = !searchTerm.trim() 
      ? [...rows] // Create a copy of rows
      : rows.filter((item) => {
          const searchValue = searchTerm.toLowerCase().trim();
          return (
            item.gap?.toLowerCase().includes(searchValue) ||
            item.recommendation?.toLowerCase().includes(searchValue) ||
            item.severity?.toLowerCase().includes(searchValue) ||
            item.type?.toLowerCase().includes(searchValue)
          );
        });
    
    // Actually, we don't need to reverse the array since in AddPotentialIssuePopup.tsx, 
    // new items are already added with unshift() which puts them at the beginning
    return result;
  }, [rows, searchTerm]);

  const handleSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value.toLowerCase().trim();
    setSearchTerm(searchValue);
    setCurrentPage(1); // Reset to first page when searching
  }, []);

  // Pagination handlers
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when changing rows per page
  }, []);

  // Calculate total pages
  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  }, [filteredRows.length, rowsPerPage]);

  // Get current page rows
  const currentPageRows = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredRows.slice(startIndex, endIndex);
  }, [filteredRows, currentPage, rowsPerPage]);

  const tableColumns: ZTableColumnProps<
    AgentSpecificTableRowData<
      AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
      MultiVendorAssessmentTypes.VENDOR_POTENTIAL_ISSUES_TABLE
    >
  >[] = useMemo(() => {
    return tableConfigs[MultiVendorAssessmentTypes.VENDOR_POTENTIAL_ISSUES_TABLE].map((column) => ({
      ...column,
      renderCell: (data) => {
        return column.renderType === RenderCellTypeEnum.CUSTOM
          ? column.renderCell?.(data)
          : renderTableComponents({
              renderType: column.renderType,
              data,
              metaData: {
                approved: [],
                edited: [],
                agentId: "",
              },
              onClickAvatar: () => { },
              fieldName: column.fieldName,
            });
      },
    }));
  }, []);

  // Defined column styles with fixed widths to prevent shifting
  const getColumnStyle = (fieldName: string) => {
    switch (fieldName) {
      case "control_title":
        return { style: { width: "220px" } };
      case "gap":
        return { style: { width: "40%" } };
      case "severity":
        return { style: { width: "10%", maxWidth: "80px" } };
      case "recommendation":
        return { style: { width: "40%"} };
      case "":  // action column
        return { style: { width: "5%"} };
      default:
        return {};
    }
  };

  return (
    <div className="h-full w-full overflow-hidden flex flex-col pb-3 bg-white">
      {/* header */}
      <div className="flex flex-row justify-between p-6 pb-4">
        <div className="flex items-center grow flex-1 gap-2">
          <span className="text-sm font-medium">Potential Issues</span>
          <div className="p-1 bg-[#F4F4F5] px-2 rounded-lg">
            <p className="text-[10px] text-[#52525B] leading-4 font-medium">
              {filteredRows.length}
            </p>
          </div>
        </div>
        <div className="flex justify-center grow flex-1 items-center">
          <div className="w-[612px]">
            <Input
              className="w-[400px] rounded-md bg-[#F4F4F5]"
              placeholder="Search by keywords or severity"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </div>
        <div className="flex-1 flex justify-end">
          {!isReportReady && (
            <ZButton
              size="sm"
              color="primary"
              variant="bordered"
              className="text-tiny px-2 h-7 rounded-md"
              onClick={() => {
                setAddIssuePopupMode({ mode: "ADD" });
              }}
            >
              Add New
            </ZButton>
          )}
        </div>
      </div>

      {/* table */}
      <div className="grow overflow-hidden flex flex-col">
        <div className="flex-1 flex flex-col bg-white overflow-auto px-6">
          <ZTableDeprecrated<IPotentialIssue>
            className="border-1 border-[#E4E4E7] rounded-lg overflow-auto scroll-auto text-[#333333] h-full"
            loading={false}
            rows={[...currentPageRows] as IPotentialIssue[]}
            columns={tableColumns}
            isHeaderSticky={true}
            isCompact={true}
            removeWrapper={true}
            tableHeaderProps={{
              className: "shadow-none rounded-none",
            }}
            tableBodyProps={{
              emptyContent: "No data found",
              className: "h-full flex items-center justify-center",
            }}
            tableColumnProps={(column, columnIndex) => {
              return getColumnStyle(column.fieldName);
            }}
            tableRowProps={(_, rowIndex) => {
              return {
                className: cn(
                  rowIndex === 0 ? "border-none" : "border-y",
                  "hover:bg-[#F2F2F2] group",
                  "border-b-1 border-b-[#E4E4E7] break-words",
                ),
                id: `table-row-${rowIndex}`,
              };
            }}
            classNames={{
              wrapper: "shadow-none rounded-none ",
              table: "table-fixed",
            }}
            aria-label="Potential Issues"
          />
        </div>

        {/* Pagination - only show when there's more than one page */}
        {totalPages > 1 && (
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            isDisabled={false}
          />
        )}
      </div>

      {/* popup */}
      {addIssuePopupMode.mode === "ADD" && (
        <AddPotentialIssuePopup
          handleClose={() => setAddIssuePopupMode({ mode: "CLOSED" })}
          type="ADD"
        />
      )}
      {addIssuePopupMode.mode === "EDIT" && (
        <AddPotentialIssuePopup
          handleClose={() => setAddIssuePopupMode({ mode: "CLOSED" })}
          type="EDIT"
          editKey={addIssuePopupMode.issueType}
          editIdx={addIssuePopupMode.indexInIssuesArray}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false })}
        onConfirm={confirmDelete}
        entityName="potential issue"
      />
    </div>
  );
};

export default PotentialIssues;

export interface IPotentialIssue {
  id: string;
  type: string;
  gap: string;
  recommendation: string;
  confidence: ConfidenceTypes;
  severity: string;
  control_title: string;

  // handler for the rows
  handleDelete: () => void;
  handleEdit: () => void;
  isReportReady: boolean;
}